const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        this.oldSearchValue = valueToSearch;

        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        clearTimeout(searchTimeoutID);

        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set(
              'resources[options][fields]',
              includeMeta
                ? 'title,product_type,variants.title,vendor,tag,variants.sku'
                : 'title,product_type,variants.title,vendor,tag'
            );
          } else {
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsList = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
              }

              searchAbortController = null;
              this.classList.remove(
                'main-search--has-results',
                'main-search--results-on-multiple-lines',
                'main-search--no-results'
              );
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              // Convert blocks into quick search format
              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                if (index <= resultLimit) {
                  block.classList.add('main-search-result');
                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                  resultsProducts.firstElementChild.appendChild(block);

                  // 🛠 Clone block for matching SKU
                  const searchSku = searchInput.value.trim().toLowerCase();
                  if (searchSku) {
                    const matchingImage = Array.from(block.querySelectorAll('.product-block__image img'))
                      .find(img => img.src.toLowerCase().includes(searchSku));

                    if (matchingImage) {
                      const cloneBlock = block.cloneNode(true);

                      // Replace image
                      const primaryWrapper = cloneBlock.querySelector('.product-block__image--primary .img-ar');
                      if (primaryWrapper) {
                        primaryWrapper.innerHTML = "";
                        primaryWrapper.appendChild(matchingImage.cloneNode(true));
                      }

                      // ✅ Fix link → exact variant
                      const productLink = cloneBlock.querySelector('a');
                      if (productLink) {
                        let variantId = null;

                        // 1. Check for data attributes
                        let variantEl = block.querySelector(
                          `[data-sku="${searchSku.toUpperCase()}"], [data-sku="${searchSku}"]`
                        );
                        if (variantEl && variantEl.dataset.variantId) {
                          variantId = variantEl.dataset.variantId;
                        }

                        // 2. Fallback: parse JSON
                        if (!variantId) {
                          const jsonEl = block.querySelector('script[type="application/json"]');
                          if (jsonEl) {
                            try {
                              const productData = JSON.parse(jsonEl.textContent);
                              const variant = productData.variants.find(v =>
                                v.sku && v.sku.toLowerCase() === searchSku
                              );
                              if (variant) {
                                variantId = variant.id;
                              }
                            } catch (e) {
                              console.warn("Could not parse product JSON", e);
                            }
                          }
                        }

                        // 3. Apply variant link
                        if (variantId) {
                          productLink.href = productLink.href.split("?")[0] + `?variant=${variantId}`;
                        }
                      }

                      // Add SKU label
                      const priceEl = cloneBlock.querySelector('.product-block__price, .price');
                      if (priceEl) {
                        const skuTag = document.createElement('div');
                        skuTag.className = "product-block__sku";
                        skuTag.style.fontSize = "0.85em";
                        skuTag.style.color = "#666";
                        skuTag.innerText = `SKU: ${searchSku.toUpperCase()}`;
                        priceEl.insertAdjacentElement('afterend', skuTag);
                      }

                      resultsProducts.firstElementChild.appendChild(cloneBlock);
                    }
                  }
                }
              });

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');
              if (areProducts || arePages) {
                this.classList.add('main-search--has-results');
                this.classList.toggle(
                  'main-search--results-on-multiple-lines',
                  resultsProducts.querySelectorAll('.product-block').length > 4
                );
                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
              } else {
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
              }
            })
            .catch((error) => {
              console.warn(error);
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        this.classList.remove(
          'main-search--has-results',
          'main-search--results-on-multiple-lines',
          'main-search--loading'
        );
        resultsBox.innerHTML = '';
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);
